import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  supabase,
  type TenantData,
  type InsertTenantData,
  type ProjectionAxis,
} from "@/utils/supabase";
import type { TenantInstance } from "@/three/config/types";
import { useAuthStore } from "@/stores/authStore";

// 쿼리 키
export const tenantQueryKeys = {
  all: ["tenants"] as const,
  building: (buildingId: string) => ["tenants", buildingId] as const,
};

/**
 * 입주자 데이터 조회
 */
async function fetchTenants(buildingId: string): Promise<TenantInstance[]> {
  const { data, error } = await supabase
    .from("tenants")
    .select(
      `
      *,
      user_profile:user_profiles!tenants_user_id_fkey (
        username,
        user_color
      )
    `
    )
    .eq("building_id", buildingId)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Error loading tenants:", error);
    throw error;
  }

  return (data || []).map(
    (
      tenant: TenantData & {
        user_profile?: { username?: string; user_color?: string };
      }
    ) => ({
      id: tenant.id,
      buildingId: tenant.building_id,
      userId: tenant.user_id,
      username: tenant.user_profile?.username || undefined,
      userColor: tenant.user_profile?.user_color || undefined,
      text: tenant.text,
      projectionAxis: tenant.projection_axis,
      positionU: tenant.position_u,
      positionV: tenant.position_v,
    })
  );
}

/**
 * 입주자 생성 함수
 */
interface CreateTenantParams {
  buildingId: string;
  text: string;
  axis: "x" | "z";
  positionU: number;
  positionV: number;
  structure: Array<{
    position: [number, number, number];
    scale: [number, number, number];
  }>;
}

async function createTenantApi(
  params: CreateTenantParams
): Promise<TenantData> {
  const user = useAuthStore.getState().user;
  const isAnonymous = useAuthStore.getState().isAnonymous();

  if (!user) {
    throw new Error("User not authenticated");
  }

  if (isAnonymous) {
    throw new Error("Anonymous users cannot create tenants");
  }

  // 축 방향 자동 결정
  const projectionAxis: ProjectionAxis = params.axis === "x" ? "x" : "z";

  const insertData: InsertTenantData = {
    building_id: params.buildingId,
    user_id: user.id,
    text: params.text.slice(0, 50),
    projection_axis: projectionAxis,
    position_u: Math.max(0, Math.min(1, params.positionU)),
    position_v: Math.max(0, Math.min(1, params.positionV)),
  };

  const { data: result, error } = await supabase
    .from("tenants")
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error("Error creating tenant:", error);
    throw error;
  }

  return result;
}

/**
 * 입주자 삭제 함수
 */
async function deleteTenantApi(tenantId: string): Promise<void> {
  const user = useAuthStore.getState().user;

  if (!user) {
    throw new Error("User not authenticated");
  }

  const { error } = await supabase
    .from("tenants")
    .delete()
    .eq("id", tenantId)
    .eq("user_id", user.id);

  if (error) {
    console.error("Error deleting tenant:", error);
    throw error;
  }
}

/**
 * 입주자 데이터 쿼리 훅
 */
export function useTenantQuery(buildingId: string | null) {
  return useQuery({
    queryKey: buildingId
      ? tenantQueryKeys.building(buildingId)
      : ["tenants", null],
    queryFn: () =>
      buildingId ? fetchTenants(buildingId) : Promise.resolve([]),
    enabled: !!buildingId,
    staleTime: 1000 * 60, // 1분
  });
}

/**
 * 입주자 생성 뮤테이션 훅
 */
export function useCreateTenantMutation(buildingId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTenantApi,
    onSuccess: () => {
      // 해당 건물의 입주자 쿼리 무효화 -> 자동 리페치
      if (buildingId) {
        queryClient.invalidateQueries({
          queryKey: tenantQueryKeys.building(buildingId),
        });
      }
    },
  });
}

/**
 * 입주자 삭제 뮤테이션 훅
 */
export function useDeleteTenantMutation(buildingId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTenantApi,
    onSuccess: () => {
      // 해당 건물의 입주자 쿼리 무효화 -> 자동 리페치
      if (buildingId) {
        queryClient.invalidateQueries({
          queryKey: tenantQueryKeys.building(buildingId),
        });
      }
    },
  });
}
